import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Toaster, toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  FileText,
  Users,
  Building2,
  Settings,
  LogOut,
  Menu,
  Home,
  ChevronRight,
  Camera,
  Upload,
  Sparkles,
  AlertTriangle,
  CheckCircle,
  Info,
  Phone,
  MessageCircle,
  RefreshCw,
  Zap,
  Shield
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sidebar Component
const SidebarNav = ({ isOpen, setIsOpen }) => {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();

  const navItems = [
    { icon: LayoutDashboard, label: 'Overview', path: '/dashboard' },
    { icon: Sparkles, label: 'AI Estimate', path: '/dashboard/ai-estimate' },
    { icon: FileText, label: 'Cost Estimates', path: '/dashboard/estimates' },
    { icon: Users, label: 'Referrals', path: '/dashboard/referrals' },
    { icon: Building2, label: 'Business Network', path: '/dashboard/businesses' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {isOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsOpen(false)} />}
      <aside className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-slate-100 transform transition-transform duration-300 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">M</span>
              </div>
              <div>
                <span className="font-semibold text-primary text-sm">MED CONNECT</span>
                <span className="text-accent font-light ml-1 text-sm">ALBANIA</span>
              </div>
            </Link>
          </div>
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                <span className="text-accent font-semibold">{user?.name?.[0]?.toUpperCase() || 'U'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
          </div>
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Menu</p>
            {navItems.map((item) => (
              <Link key={item.path} to={item.path} onClick={() => setIsOpen(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive(item.path) ? 'bg-accent/10 text-accent' : 'text-muted-foreground hover:bg-slate-50 hover:text-primary'}`}>
                <item.icon className="w-5 h-5" />{item.label}
                {item.label === 'AI Estimate' && <Badge className="ml-auto bg-accent text-white text-xs">NEW</Badge>}
              </Link>
            ))}
            {isAdmin && (
              <div className="pt-4 mt-4 border-t border-slate-100">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">Admin</p>
                <Link to="/admin" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-slate-50 hover:text-primary">
                  <Settings className="w-5 h-5" />Admin Panel
                </Link>
              </div>
            )}
          </nav>
          <div className="p-4 border-t border-slate-100 space-y-2">
            <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-slate-50 hover:text-primary">
              <Home className="w-5 h-5" />Back to Home
            </Link>
            <button onClick={logout} className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 w-full">
              <LogOut className="w-5 h-5" />Sign Out
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Photo Tips Component
const PhotoTips = () => (
  <div className="bg-blue-50 rounded-xl p-4 space-y-3">
    <h4 className="font-medium text-primary flex items-center gap-2">
      <Info className="w-4 h-4" /> Tips for Best Results
    </h4>
    <ul className="text-sm text-muted-foreground space-y-2">
      <li className="flex items-start gap-2">
        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
        Good lighting - face a window or light source
      </li>
      <li className="flex items-start gap-2">
        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
        Open mouth wide to show all teeth
      </li>
      <li className="flex items-start gap-2">
        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
        Take multiple angles: front, left, right
      </li>
      <li className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
        Avoid blurry or dark photos
      </li>
    </ul>
  </div>
);

// Result Card Component
const TreatmentCard = ({ treatment }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className={`p-4 rounded-lg border ${treatment.needs_verification ? 'border-yellow-200 bg-yellow-50' : 'border-slate-100 bg-white'}`}
  >
    <div className="flex justify-between items-start mb-2">
      <div>
        <h4 className="font-medium text-primary">{treatment.treatment}</h4>
        <p className="text-sm text-muted-foreground">{treatment.location}</p>
      </div>
      <Badge className={treatment.confidence >= 75 ? 'bg-green-100 text-green-700' : treatment.confidence >= 60 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}>
        {treatment.confidence}% confidence
      </Badge>
    </div>
    <p className="text-xs text-muted-foreground mb-3">{treatment.reasoning}</p>
    <div className="flex justify-between items-center pt-2 border-t border-slate-100">
      <span className="text-sm text-muted-foreground">
        {treatment.affected_teeth} {treatment.affected_teeth === 1 ? 'tooth' : 'teeth'} × €{treatment.unit_price}
      </span>
      <span className="font-bold text-accent">€{treatment.total_price}</span>
    </div>
    {treatment.needs_verification && (
      <p className="text-xs text-yellow-600 mt-2 flex items-center gap-1">
        <AlertTriangle className="w-3 h-3" /> Needs manual dentist verification
      </p>
    )}
  </motion.div>
);

// Main AI Estimate Content
const AIEstimateContent = () => {
  const { user } = useAuth();
  const [step, setStep] = useState('upload'); // upload, analyzing, results
  const [selectedImages, setSelectedImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState(null);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [contactInfo, setContactInfo] = useState({ name: user?.name || '', email: user?.email || '', phone: '', consent: false });
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const fileInputRef = useRef(null);

  // Fetch estimate history on mount
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await fetch(`${API_URL}/api/ai-estimate/history`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setHistory(data.slice(0, 3)); // Keep only last 3
        }
      } catch (error) {
        console.error('Failed to fetch history:', error);
      } finally {
        setLoadingHistory(false);
      }
    };
    fetchHistory();
  }, []);

  const loadHistoryEstimate = (estimate) => {
    setResults({
      estimate_id: estimate.estimate_id,
      image_quality: estimate.image_quality,
      quality_issues: estimate.quality_issues || [],
      detected_treatments: estimate.detected_treatments || [],
      subtotal: estimate.subtotal,
      suggested_packages: estimate.suggested_packages || [],
      total_estimate: estimate.total_estimate,
      observations: estimate.ai_observations,
      disclaimer: "This is an AI-generated preliminary estimate and not a medical diagnosis. Final confirmation required via X-ray and clinical examination.",
      created_at: estimate.created_at
    });
    setStep('results');
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length + selectedImages.length > 3) {
      toast.error('Maximum 3 photos allowed');
      return;
    }

    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'image/heic', 'image/webp'];
      const maxSize = 10 * 1024 * 1024; // 10MB
      
      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name}: Invalid format. Use JPG, PNG, or HEIC`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name}: File too large. Max 10MB`);
        return false;
      }
      return true;
    });

    setSelectedImages(prev => [...prev, ...validFiles]);
    
    // Create preview URLs
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrls(prev => [...prev, e.target.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const analyzeImages = async () => {
    if (selectedImages.length === 0) {
      toast.error('Please upload at least one photo');
      return;
    }

    setIsAnalyzing(true);
    setStep('analyzing');

    try {
      // Convert first image to base64
      const imageBase64 = previewUrls[0];
      
      const response = await fetch(`${API_URL}/api/ai-estimate/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ image_base64: imageBase64 })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Analysis failed');
      }

      if (!data.success) {
        toast.error(data.message || 'Image quality too low');
        setStep('upload');
        return;
      }

      setResults(data);
      setStep('results');
      toast.success('Analysis complete!');
    } catch (error) {
      console.error('Analysis error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Analysis failed. Please try again.';
      toast.error(errorMessage);
      setStep('upload');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const submitForReview = async () => {
    if (!contactInfo.consent) {
      toast.error('Please accept the terms to proceed');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/ai-estimate/${results.estimate_id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(contactInfo)
      });

      if (!response.ok) throw new Error('Submission failed');

      toast.success('Estimate submitted! Our team will contact you soon.');
      setShowSubmitDialog(false);
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    }
  };

  const resetAnalysis = () => {
    setStep('upload');
    setSelectedImages([]);
    setPreviewUrls([]);
    setResults(null);
  };

  return (
    <div className="space-y-6" data-testid="ai-estimate-page">
      <div>
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-2xl font-semibold text-primary">AI Dental Estimator</h1>
          <Badge className="bg-accent text-white"><Zap className="w-3 h-3 mr-1" />Instant</Badge>
        </div>
        <p className="text-muted-foreground">Get an instant AI-powered cost estimate from your dental photos</p>
      </div>

      <AnimatePresence mode="wait">
        {step === 'upload' && (
          <motion.div
            key="upload"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            className="grid lg:grid-cols-2 gap-6"
          >
            {/* Left: Upload Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="w-5 h-5 text-accent" />
                  Upload Dental Photos
                </CardTitle>
                <CardDescription>Take or upload clear photos of your teeth (max 3)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div 
                  className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center hover:border-accent transition-colors cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/heic,image/webp"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="font-medium text-primary">Click to upload or drag and drop</p>
                  <p className="text-sm text-muted-foreground mt-1">JPG, PNG, HEIC up to 10MB</p>
                </div>

                {/* Preview Images */}
                {previewUrls.length > 0 && (
                  <div className="grid grid-cols-3 gap-3">
                    {previewUrls.map((url, index) => (
                      <div key={index} className="relative group">
                        <img src={url} alt={`Preview ${index + 1}`} className="w-full h-24 object-cover rounded-lg" />
                        <button
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ×
                        </button>
                        <Badge className="absolute bottom-1 left-1 text-xs">
                          {index === 0 ? 'Front' : index === 1 ? 'Left' : 'Right'}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}

                <Button 
                  onClick={analyzeImages} 
                  disabled={selectedImages.length === 0}
                  className="w-full h-12 rounded-full bg-accent hover:bg-accent/90"
                  data-testid="analyze-btn"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Analyze with AI
                </Button>
              </CardContent>
            </Card>

            {/* Right: Tips Section */}
            <div className="space-y-4">
              <PhotoTips />
              
              {/* Previous Estimates */}
              {!loadingHistory && history.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="w-4 h-4 text-accent" />
                      Recent Estimates
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {history.map((est) => (
                      <button
                        key={est.estimate_id}
                        onClick={() => loadHistoryEstimate(est)}
                        className="w-full text-left p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm font-medium text-primary">
                              {est.detected_treatments?.length || 0} treatments detected
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(est.created_at).toLocaleDateString()} at {new Date(est.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-accent">€{est.total_estimate}</p>
                            <Badge className={est.status === 'submitted_for_review' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'} variant="secondary">
                              {est.status === 'submitted_for_review' ? 'Submitted' : 'Draft'}
                            </Badge>
                          </div>
                        </div>
                      </button>
                    ))}
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Shield className="w-8 h-8 text-accent flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-primary">Privacy Protected</h4>
                      <p className="text-sm text-muted-foreground">Your photos are analyzed securely and never shared without consent.</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="bg-gradient-to-br from-primary to-primary/80 rounded-xl p-6 text-white">
                <h4 className="font-semibold mb-2">Save up to 70%</h4>
                <p className="text-sm text-white/80">Clinics in Tirana offer premium dental care at a fraction of Western European prices.</p>
              </div>
            </div>
          </motion.div>
        )}

        {step === 'analyzing' && (
          <motion.div
            key="analyzing"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex flex-col items-center justify-center py-16"
          >
            <div className="w-20 h-20 rounded-full bg-accent/10 flex items-center justify-center mb-6">
              <Sparkles className="w-10 h-10 text-accent animate-pulse" />
            </div>
            <h3 className="text-xl font-semibold text-primary mb-2">Analyzing Your Photos</h3>
            <p className="text-muted-foreground mb-6">Our AI is examining your dental condition...</p>
            <Progress value={66} className="w-64 h-2" />
            <p className="text-sm text-muted-foreground mt-4">This usually takes 5-10 seconds</p>
          </motion.div>
        )}

        {step === 'results' && results && (
          <motion.div
            key="results"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Image Quality Banner */}
            {results.image_quality !== 'good' && results.quality_issues?.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-yellow-800">Image Quality Notice</h4>
                  <p className="text-sm text-yellow-700">{results.quality_issues.join('. ')}</p>
                </div>
              </div>
            )}

            <div className="grid lg:grid-cols-3 gap-6">
              {/* Left: Detected Treatments */}
              <div className="lg:col-span-2 space-y-4">
                <h3 className="font-semibold text-primary">Detected Treatments</h3>
                
                {results.detected_treatments?.length > 0 ? (
                  <div className="space-y-3">
                    {results.detected_treatments.map((treatment, index) => (
                      <TreatmentCard key={index} treatment={treatment} />
                    ))}
                  </div>
                ) : (
                  <Card>
                    <CardContent className="py-8 text-center">
                      <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-3" />
                      <h4 className="font-medium text-primary">No Major Issues Detected</h4>
                      <p className="text-sm text-muted-foreground">Your teeth appear to be in good condition based on the photo.</p>
                    </CardContent>
                  </Card>
                )}

                {/* Observations */}
                {results.observations && (
                  <div className="bg-slate-50 rounded-xl p-4">
                    <h4 className="font-medium text-primary mb-2">Additional Observations</h4>
                    <p className="text-sm text-muted-foreground">{results.observations}</p>
                  </div>
                )}
              </div>

              {/* Right: Summary & Actions */}
              <div className="space-y-4">
                {/* Price Summary */}
                <Card className="border-2 border-accent">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Estimated Cost</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-4xl font-bold text-accent mb-4">€{results.total_estimate}</div>
                    
                    {results.suggested_packages?.length > 0 && (
                      <div className="bg-green-50 rounded-lg p-3 mb-4">
                        <h5 className="font-medium text-green-800 text-sm">{results.suggested_packages[0].name}</h5>
                        <p className="text-xs text-green-600">{results.suggested_packages[0].savings}</p>
                      </div>
                    )}

                    <Button 
                      onClick={() => setShowSubmitDialog(true)}
                      className="w-full rounded-full bg-accent hover:bg-accent/90 mb-3"
                      data-testid="request-quote-btn"
                    >
                      Request Verified Quote
                    </Button>
                    
                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1" asChild>
                        <a href="tel:+355686089622">
                          <Phone className="w-4 h-4 mr-1" /> Call
                        </a>
                      </Button>
                      <Button variant="outline" className="flex-1" asChild>
                        <a href="https://wa.me/355686089622" target="_blank" rel="noopener noreferrer">
                          <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Disclaimer */}
                <div className="bg-slate-50 rounded-xl p-4 text-xs text-muted-foreground">
                  <h5 className="font-medium text-primary mb-2 flex items-center gap-1">
                    <Info className="w-3 h-3" /> Important Notice
                  </h5>
                  <p>{results.disclaimer}</p>
                </div>

                {/* Reset Button */}
                <Button variant="outline" onClick={resetAnalysis} className="w-full">
                  <RefreshCw className="w-4 h-4 mr-2" /> Analyze Another Photo
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Submit Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Verified Quote</DialogTitle>
            <DialogDescription>Our dental experts will review your case and provide a detailed quote.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Full Name</Label>
              <Input value={contactInfo.name} onChange={(e) => setContactInfo({...contactInfo, name: e.target.value})} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={contactInfo.email} onChange={(e) => setContactInfo({...contactInfo, email: e.target.value})} />
            </div>
            <div>
              <Label>Phone / WhatsApp</Label>
              <Input value={contactInfo.phone} onChange={(e) => setContactInfo({...contactInfo, phone: e.target.value})} placeholder="+355 69 XXX XXXX" />
            </div>
            <div className="flex items-start gap-2">
              <Checkbox 
                id="consent" 
                checked={contactInfo.consent}
                onCheckedChange={(checked) => setContactInfo({...contactInfo, consent: checked})}
              />
              <label htmlFor="consent" className="text-sm text-muted-foreground">
                I consent to MED CONNECT ALBANIA processing my data and photos for the purpose of providing a dental treatment quote. GDPR compliant.
              </label>
            </div>
            <Button onClick={submitForReview} className="w-full" disabled={!contactInfo.consent}>
              Submit for Review
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Page Component
export default function AIEstimatePage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-center" richColors />
      <SidebarNav isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      
      <header className="sticky top-0 z-30 bg-white border-b border-slate-100 lg:pl-64">
        <div className="flex items-center justify-between px-4 md:px-8 h-16">
          <div className="flex items-center gap-4">
            <button className="lg:hidden p-2 -ml-2" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-2 text-sm">
              <Link to="/dashboard" className="text-muted-foreground hover:text-primary">Dashboard</Link>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium">AI Dental Estimator</span>
            </div>
          </div>
        </div>
      </header>
      
      <main className="lg:pl-64 pt-6 pb-12 px-4 md:px-8">
        <AIEstimateContent />
      </main>
    </div>
  );
}
